import { useLang } from "@/context/LanguageContext";
import { ArrowUpRight } from "lucide-react";
import BrandMarkWatermark from "@/components/BrandMarkWatermark";

const PARENT_MARK = "https://dealmedaseligmann.ch/brand/mark-red.png";
const PARENT_URL = "https://www.dealmedaseligmann.ch/";

export default function GroupHolding() {
  const { t } = useLang();
  const g = t.group;

  return (
    <section id="group" className="relative section-y overflow-hidden bg-[color:var(--anthracite)]" data-testid="group-section">
      {/* Subtle architectural motif — copper hairlines */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]">
        <div className="absolute inset-y-0 left-[8%] w-px bg-[color:var(--bronze)]" />
        <div className="absolute inset-y-0 left-[32%] w-px bg-[color:var(--bronze)]" />
        <div className="absolute inset-y-0 left-[64%] w-px bg-[color:var(--bronze)]" />
        <div className="absolute inset-y-0 left-[92%] w-px bg-[color:var(--bronze)]" />
      </div>
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[color:var(--copper)]/10 blur-3xl pointer-events-none" />
      <BrandMarkWatermark size={820} tone="light" opacity={0.11} style={{ top: -120, right: -100, zIndex: 1 }} />

      <div className="container-lux relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left column — parent brand + CTA */}
          <div className="lg:col-span-5">
            <div className="overline" style={{ color: "var(--bronze)" }} data-testid="group-overline">
              {g.overline}
            </div>
            <h2
              className="mt-6 font-serif-display text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.05] tracking-[-0.01em] text-[color:var(--bone)]"
              data-testid="group-title"
            >
              {g.title}
            </h2>

            <div className="mt-10 flex items-start gap-4 p-6 border border-[color:var(--copper)]/25 bg-[color:var(--charcoal)] max-w-md">
              <div className="flex-none w-14 h-14 flex items-center justify-center bg-[color:var(--bone)] p-2">
                <img
                  src={PARENT_MARK}
                  alt="De Almeda Seligmann Capital AG"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="min-w-0">
                <div className="font-serif-display text-[18px] text-[color:var(--bone)] leading-tight">
                  De Almeda Seligmann Capital AG
                </div>
                <div className="mt-1 text-[11px] tracking-[0.22em] uppercase text-[color:var(--bronze)]">
                  {g.location}
                </div>
              </div>
            </div>

            <a
              href={PARENT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-3 bg-[color:var(--copper)] hover:bg-[color:var(--bronze)] text-[color:var(--bone)] px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-medium transition-colors duration-300 group"
              data-testid="group-parent-link"
            >
              {g.visit_cta}
              <ArrowUpRight
                size={16}
                strokeWidth={1.25}
                className="group-hover:rotate-45 transition-transform duration-500"
              />
            </a>
          </div>

          {/* Right column — description + pillars */}
          <div className="lg:col-span-7">
            <p
              className="text-[17px] leading-[1.75] text-[color:var(--bone)]/80 max-w-2xl"
              data-testid="group-body"
            >
              {g.body}
            </p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 border-t border-[color:var(--copper)]/25 pt-10">
              {g.pillars.map((p, i) => (
                <div key={p.title} className="relative" data-testid={`group-pillar-${i}`}>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--bronze)]">
                    0{i + 1}
                  </div>
                  <h3 className="mt-3 font-serif-display text-[22px] leading-tight text-[color:var(--bone)]">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[14px] leading-[1.7] text-[color:var(--bone)]/70">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
